import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { communityApi } from "lib/api/community";
import {
  getCommunityUiStrings,
  resolveCommunityLocale,
} from "i18n/communityUi";
import {
  DEFAULT_COMMUNITY_DAYS,
} from "../utils";

/**
 * Centralized server-state hook for the enterprise community dashboard.
 * Keeps data fetching, locale, and shared formatters in one place so the page
 * remains a thin orchestrator.
 */
export function useCommunityDashboard() {
  const [days, setDays] = useState<number>(DEFAULT_COMMUNITY_DAYS);
  const locale = useMemo(() => resolveCommunityLocale(), []);
  const labels = useMemo(() => getCommunityUiStrings(locale), [locale]);

  const overviewQuery = useQuery({
    queryKey: ["community", "overview", days],
    queryFn: () => communityApi.getOverview({ days }),
  });
  const pipelineQuery = useQuery({
    queryKey: ["community", "pipeline"],
    queryFn: () => communityApi.getPipeline(),
  });
  const activityTrendQuery = useQuery({
    queryKey: ["community", "activity-trend", days],
    queryFn: () => communityApi.getActivityTrend({ days }),
  });
  const issueActivityQuery = useQuery({
    queryKey: ["community", "issue-activity", days],
    queryFn: () => communityApi.getIssueActivity({ days, limit: 25 }),
  });
  const leaderboardQuery = useQuery({
    queryKey: ["community", "leaderboard", days],
    queryFn: () => communityApi.getLeaderboard({ days, limit: 12 }),
  });
  const governanceQuery = useQuery({
    queryKey: ["community", "governance", days],
    queryFn: () => communityApi.getGovernanceHealth({ days }),
  });
  const alignmentQuery = useQuery({
    queryKey: ["community", "alignment", days],
    queryFn: () => communityApi.getAlignment({ days }),
  });
  const issueHealthQuery = useQuery({
    queryKey: ["community", "issue-health"],
    queryFn: () => communityApi.getIssueHealth(),
  });
  const demographicsQuery = useQuery({
    queryKey: ["community", "demographics"],
    queryFn: () => communityApi.getDemographics({ limit: 18 }),
  });

  const numberFormatter = useMemo(() => new Intl.NumberFormat(locale), [locale]);
  const compactNumberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    [locale]
  );
  const dateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: "short",
        day: "numeric",
      }),
    [locale]
  );
  const regionDisplayNames = useMemo(() => {
    if (typeof Intl.DisplayNames === "undefined") {
      return null;
    }

    return new Intl.DisplayNames([locale], { type: "region" });
  }, [locale]);

  return {
    days,
    setDays,
    locale,
    labels,
    numberFormatter,
    compactNumberFormatter,
    dateFormatter,
    regionDisplayNames,
    overviewQuery,
    pipelineQuery,
    activityTrendQuery,
    issueActivityQuery,
    leaderboardQuery,
    governanceQuery,
    alignmentQuery,
    issueHealthQuery,
    demographicsQuery,
  };
}

